import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { AlertTriangle, MessageSquare, DollarSign, Brain } from 'lucide-react';

const LimitationsSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Understanding the boundaries of current reasoning models"
      >
        Current Limitations
      </SlideTitle>
      
      <SlideContent>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Hallucination */}
          <div className="slide-card border-destructive/30">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <span className="font-heading font-semibold text-foreground">Hallucination in Technical Details</span>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-destructive/10">
                <p className="text-xs text-muted-foreground mb-1">Bad Response:</p>
                <p className="text-sm text-foreground">"The MTU on GigabitEthernet0/1 is 1500 bytes."</p>
                <p className="text-xs text-destructive mt-1">Model doesn't have access to actual config</p>
              </div>
              
              <div className="p-3 rounded-lg bg-accent/10">
                <p className="text-xs text-muted-foreground mb-1">Good Response:</p>
                <p className="text-sm text-foreground">"Please run: show interface GigabitEthernet0/1 | include MTU"</p>
                <p className="text-xs text-accent mt-1">Acknowledges data limitation</p>
              </div>
            </div>
          </div>
          
          {/* Benchmaxxing */}
          <div className="slide-card border-chart-4/30">
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="w-5 h-5 text-chart-4" />
              <span className="font-heading font-semibold text-foreground">Benchmark Saturation</span>
            </div>
            
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-chart-4">•</span>
                Models optimize for test performance, not real-world capability
              </li>
              <li className="flex items-start gap-2">
                <span className="text-chart-4">•</span>
                Math benchmarks ≠ network troubleshooting ability
              </li>
              <li className="flex items-start gap-2">
                <span className="text-chart-4">•</span>
                Need domain-specific evaluation
              </li>
            </ul>
          </div>
          
          {/* Cost trade-off */}
          <div className="slide-card border-primary/30">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="w-5 h-5 text-primary" />
              <span className="font-heading font-semibold text-foreground">Cost vs. Accuracy Trade-off</span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Base LLM:</span>
                <span className="font-mono text-foreground">$0.002, 1s, 70%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reasoning:</span>
                <span className="font-mono text-foreground">$0.02, 10s, 90%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Self-consistency (5x):</span>
                <span className="font-mono text-foreground">$0.10, 50s, 95%</span>
              </div>
            </div>
            
            <p className="mt-4 text-xs text-primary">
              When is 95% worth 50x cost?
            </p>
          </div>
          
          {/* No true understanding */}
          <div className="slide-card border-secondary/30">
            <div className="flex items-center gap-3 mb-4">
              <Brain className="w-5 h-5 text-secondary" />
              <span className="font-heading font-semibold text-foreground">No True "Understanding"</span>
            </div>
            
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-secondary">•</span>
                Models pattern-match, don't truly understand protocols
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary">•</span>
                Can fail on novel scenarios outside training
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary">•</span>
                Still need human verification for critical decisions
              </li>
            </ul>
          </div>
        </div>
        
        <div className="mt-6 p-4 rounded-xl bg-muted/30 border border-border">
          <p className="text-sm text-center text-muted-foreground">
            <strong className="text-foreground">Key Message:</strong> Reasoning models are powerful tools, but they're co-pilots, not autopilots.
          </p>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default LimitationsSlide;
